import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { useAuth } from '../context/AuthContext';
import { useNavigate, useLocation } from 'react-router-dom';
import { FaEnvelope, FaArrowRight, FaHeart } from 'react-icons/fa';

const PageContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 100vh;
  width: 100%;
  padding: 20px;
  background-color: #fcfaf7;
  background-image: linear-gradient(rgba(255, 255, 255, 0.8), rgba(255, 255, 255, 0.8)),
    url('/img/background-floral.jpg');
  background-size: cover;
  background-position: center;
  box-sizing: border-box;
`;

const LoginCard = styled(motion.div)`
  background-color: white;
  border-radius: 10px;
  box-shadow: 0 4px 20px rgba(148, 112, 96, 0.15);
  padding: 40px 30px;
  width: 100%;
  max-width: 400px;
  text-align: center;
  
  @media (max-width: 480px) {
    padding: 30px 20px;
  }
`;

const Title = styled.h1`
  font-family: 'Cormorant Garamond', serif;
  color: #947060;
  font-size: 2.2rem;
  margin-top: 0;
  margin-bottom: 10px;
  font-weight: 500;
`;

const NameSpan = styled.span`
  display: block;
  font-family: 'Great Vibes', cursive;
  font-size: 2rem;
  margin: 10px 0 30px;
  color: #b38b6d;
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  width: 100%;
`;

const InputContainer = styled.div`
  position: relative;
  margin-bottom: 25px;
`;

const Input = styled.input`
  width: 100%;
  padding: 12px 15px 12px 45px;
  border: 1px solid ${props => props.hasError ? '#e74c3c' : '#d4b59e'};
  border-radius: 50px;
  font-family: 'Lato', sans-serif;
  font-size: 1rem;
  color: #555;
  box-sizing: border-box;
  transition: all 0.3s ease;
  
  &:focus {
    outline: none;
    border-color: #947060;
    box-shadow: 0 0 0 2px rgba(148, 112, 96, 0.2);
  }
`;

const InputIcon = styled.div`
  position: absolute;
  left: 15px;
  top: 50%;
  transform: translateY(-50%);
  color: #947060;
  font-size: 1.2rem;
`;

const Button = styled.button`
  background-color: #947060;
  color: white;
  border: none;
  border-radius: 50px;
  padding: 12px 20px;
  font-family: 'Lato', sans-serif;
  font-size: 1rem;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;
  
  &:hover {
    background-color: #85655c;
    transform: translateY(-2px);
  }
  
  &:disabled {
    background-color: #ccc;
    cursor: not-allowed;
    transform: translateY(0);
  }
`;

const ButtonText = styled.span`
  margin-right: 8px;
`;

const ErrorMessage = styled.p`
  color: #e74c3c;
  font-family: 'Lato', sans-serif;
  font-size: 0.9rem;
  margin: 5px 0 15px;
  text-align: center;
`;

const InfoText = styled.p`
  color: #666;
  font-family: 'Lato', sans-serif;
  font-size: 0.9rem;
  margin-top: 25px;
  line-height: 1.5;
`;

const HeartIcon = styled(FaHeart)`
  font-size: 1.2rem;
  color: #d4b59e;
  margin: 15px 0;
`;


const Login = () => {
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { login, currentUser, loading, error } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  
  // Redirect if user is already logged in
  useEffect(() => {
    if (currentUser) {
      const redirectPath = location.state?.from || '/';
      navigate(redirectPath);
    }
  }, [currentUser, navigate, location]);
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    
    setIsSubmitting(true);
    console.log("Submitting login for email:", email);
    
    try {
      const success = await login(email);
      console.log("Login result:", success);
      
      if (success) {
        console.log("Login successful, redirecting to home page");
        // Always redirect to home page regardless of original requested path
        navigate("/", { replace: true });
      }
    } catch (err) {
      console.error("Error in handleSubmit:", err);
    } finally {
      setIsSubmitting(false);
    }
  };
  
  if (loading) {
    return (
      <PageContainer>
        <LoginCard>
          <Title>Loading...</Title>
        </LoginCard>
      </PageContainer>
    );
  }
  
  return (
    <PageContainer>
      <LoginCard
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
      >
        <Title>Welcome to Our Wedding</Title>
        <NameSpan>Erika & Andrew</NameSpan>
        
        <Form onSubmit={handleSubmit}>
          <InputContainer>
            <InputIcon>
              <FaEnvelope />
            </InputIcon>
            <Input 
              type="email"
              placeholder="Enter your email address"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              hasError={!!error}
              required
            />
          </InputContainer>
          
          {error && <ErrorMessage>{error}</ErrorMessage>}
          
          <Button 
            type="submit"
            disabled={isSubmitting || !email.trim()}
          >
            <ButtonText>{isSubmitting ? 'Checking...' : 'Enter Wedding Site'}</ButtonText>
            {!isSubmitting && <FaArrowRight />}
          </Button>
        </Form>
        
        <HeartIcon />
        
        <InfoText>
          Please enter the email address you filled out the Save the Date with.
          <br />
          Please contact Erika or Andrew if you're having trouble!
        </InfoText>
      </LoginCard>
    </PageContainer>
  );
};

export default Login;